import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useEmployees } from '../hooks/useEmployees';
import { useNotification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Modal } from '../components/UI/Modal';

export const EmployeesPage: React.FC = observer(() => {
  const employeeStore = useEmployees();
  const [showArchived, setShowArchived] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { success, error } = useNotification();
  const { t } = useLanguage();

  const [newEmployeeName, setNewEmployeeName] = useState('');

  useEffect(() => {
    employeeStore.loadEmployees();
  }, [employeeStore]);

  const handleCreateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmployeeName.trim()) {
      error(t('employees.errorValidation'), t('employees.errorFillAllFields'));
      return;
    }

    const result = await employeeStore.createEmployee(newEmployeeName);
    if (result) {
      success(t('employees.success'), t('employees.successCreated'));
      setNewEmployeeName('');
      setIsModalOpen(false);
    } else {
      error(t('employees.error'), t('employees.errorCreateFailed'));
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    const result = await employeeStore.updateEmployeeStatus(id, !currentStatus);
    if (result) {
      success(
        t('employees.success'),
        currentStatus ? t('employees.successArchived') : t('employees.successActivated')
      );
    } else {
      error(t('employees.error'), t('employees.errorUpdateFailed'));
    }
  };

  const filteredEmployees = showArchived
    ? employeeStore.archivedEmployees
    : employeeStore.activeEmployees;

  if (employeeStore.loading) {
    return (
      <div className="container-app py-8">
        <div className="text-center text-text-secondary">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="container-app py-8">
      {/* Заголовок и описание */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">{t('employees.title')}</h1>
        <p className="text-text-secondary">{t('employees.subtitle')}</p>
      </div>

      {/* Кнопки управления */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
        <button
          onClick={() => setShowArchived(!showArchived)}
          className="btn btn-secondary"
        >
          {showArchived ? t('employees.showActive') : t('employees.showArchived')}
        </button>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn btn-primary"
        >
          {t('employees.addNew')}
        </button>
      </div>

      {/* Статистические карточки */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="stat-card">
          <div className="stat-card-label">{t('employees.activeCount')}</div>
          <div className="stat-card-value">{employeeStore.activeEmployees.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">{t('employees.archivedCount')}</div>
          <div className="stat-card-value text-text-secondary">{employeeStore.archivedEmployees.length}</div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewEmployeeName('');
        }}
        title={t('employees.formTitle')}
        size="md"
      >
        <form onSubmit={handleCreateEmployee} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('employees.fieldName')}
            </label>
            <input
              type="text"
              value={newEmployeeName}
              onChange={(e) => setNewEmployeeName(e.target.value)}
              className="input"
              placeholder={t('employees.placeholderName')}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setNewEmployeeName('');
              }}
              className="btn btn-secondary"
            >
              {t('employees.cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('employees.create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Таблица сотрудников */}
      <div className="card">
        <div className="overflow-x-auto">
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-text-secondary">
              {showArchived ? t('employees.noArchived') : t('employees.noActive')}
            </div>
          ) : (
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-background-300">
                  <th className="table-header w-20">ID</th>
                  <th className="table-header">{t('employees.columnName')}</th>
                  <th className="table-header">{t('employees.columnStatus')}</th>
                  <th className="table-header text-right">{t('employees.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee, index) => (
                  <tr key={employee.id} className="table-row">
                    <td className="table-cell">
                      <span className="text-text-tertiary font-medium">#{index + 1}</span>
                    </td>
                    <td className="table-cell font-medium">
                      {employee.name}
                    </td>
                    <td className="table-cell">
                      <span className={employee.is_active ? 'badge-success' : 'badge-error'}>
                        {employee.is_active ? t('employees.statusActive') : t('employees.statusArchived')}
                      </span>
                    </td>
                    <td className="table-cell text-right">
                      <button
                        onClick={() => handleToggleStatus(employee.id, employee.is_active)}
                        className={employee.is_active ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
                      >
                        {employee.is_active ? t('employees.actionArchive') : t('employees.actionActivate')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
});
